/**
 * PreparedDropsDraftsList state and handlers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@/providers/ToastProvider';
import { useStaticTags } from '@/hooks';

import type { PreparedDropsDraftsListProps, SentSection, SortKey, TabKind } from '../shared/types';
import {
  PREPARED_DRAFTS_BULK_DELETE_CONFIRM_ABOVE_COUNT,
  PREPARED_DRAFTS_UNDOABLE_MAX_COUNT,
  bulkDeleteConfirmMessage,
} from '../shared/prepared-drafts-bulk-delete.constants';
import { buildCatalogResolvers, catalogCompositeKey } from '../shared/compositeCatalogLookups';
import {
  classifyPreparedDrop,
  filterDropsBySearch,
  filterDropsByStaticTag,
  filterDropsByTag,
  sortDrops,
  sortDropsGroupedByCountry,
} from '../shared/filter';
import { runBulkSendViaCallback, runBulkSendViaStream } from '../shared/bulk';

import { usePreparedDropInvestigationHighlight } from './use-prepared-drop-investigation-highlight';
import { useDraftRowDragSelection } from './useDraftRowDragSelection';

export type UsePreparedDropsDraftsListParams = PreparedDropsDraftsListProps;

export function usePreparedDropsDraftsList(props: UsePreparedDropsDraftsListParams) {
  const {
    countryKey,
    wiserId,
    workDate,
    defaultSubTab = 'drafts',
    drops,
    gateways,
    leadsLists,
    perfTagsQuery,
    dropListTags,
    onDelete,
    onDuplicateDraft,
    onKillSent,
    onCopyAsDraft,
    onBulkSendComplete,
    onBulkSend,
    onBeforeEditSent,
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
    countryScopedCatalog = false,
    gatewaysWithCountry = [],
    leadsWithCountry = [],
    showCountryColumn = false,
    countryFilterOptions,
  } = props;

  const [activeTab, setActiveTab] = useState<TabKind>(defaultSubTab);
  const [sentSection, setSentSection] = useState<SentSection>('sent');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Drafts default to Planned send (soonest first); the Sent tab overrides to time/desc on entry.
  const [sortBy, setSortBy] = useState<SortKey | null>(
    defaultSubTab === 'sent' ? 'time' : 'scheduled',
  );
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    defaultSubTab === 'sent' ? 'desc' : 'asc',
  );
  const [editingSentDropIds, setEditingSentDropIds] = useState<string[]>([]);
  const [showCopyAsDraft, setShowCopyAsDraft] = useState(false);
  const [showDuplicateDraft, setShowDuplicateDraft] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [showMultiCountryEdit, setShowMultiCountryEdit] = useState(false);
  const [multiCountrySentEditIds, setMultiCountrySentEditIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [staticTagFilters, setStaticTagFilters] = useState<string[]>([]);
  const [countryKeysFilter, setCountryKeysFilter] = useState<string[]>([]);
  const [smswiserDraftsOnly, setSmswiserDraftsOnly] = useState(false);
  const [scheduledGroupByCountry, setScheduledGroupByCountry] = useState(false);
  const [bulkSendStatus, setBulkSendStatus] = useState<{
    running: boolean;
    total: number;
    sent: number;
    failed: number;
  }>({ running: false, total: 0, sent: 0, failed: 0 });

  const { success: toastSuccess, error: toastError, loading: toastLoading, update: toastUpdate, dismiss: toastDismiss } =
    useToast();

  useEffect(() => {
    if (defaultSubTab === 'sent') setActiveTab('sent');
  }, [defaultSubTab]);

  useEffect(() => {
    setSelectedIds([]);
    setMultiCountrySentEditIds([]);
  }, [workDate]);

  useEffect(() => {
    if (!showCountryColumn) setScheduledGroupByCountry(false);
  }, [showCountryColumn]);

  const catalog = useMemo(
    () =>
      buildCatalogResolvers(
        countryScopedCatalog,
        gateways,
        leadsLists,
        gatewaysWithCountry,
        leadsWithCountry,
      ),
    [countryScopedCatalog, gateways, leadsLists, gatewaysWithCountry, leadsWithCountry],
  );

  const fallbackTagsForDrop = useCallback(
    (d: import('@/features/smswiser-campaigns/types/workspace.types').PreparedDrop) => {
      const raw = perfTagsQuery?.data?.tagsByWiserId;
      if (!raw) return undefined;
      if (countryScopedCatalog) {
        const key = catalogCompositeKey(d.countryKey, d.leadsListWiserId);
        return (raw as Record<string, string[]>)[key];
      }
      return raw[d.leadsListWiserId];
    },
    [perfTagsQuery?.data?.tagsByWiserId, countryScopedCatalog],
  );
  /** Per-campaign resolution when the host provides it (Prepared Drops tab):
   *  display, filter and filter options all agree on the same resolver. */
  const tagsForDrop = dropListTags?.tagsForDrop ?? fallbackTagsForDrop;

  const draftDrops = useMemo(() => {
    let list = drops.filter((d) => d.status === 'DRAFT');
    if (showCountryColumn && countryKeysFilter.length > 0) {
      const set = new Set(countryKeysFilter);
      list = list.filter((d) => set.has(d.countryKey));
    }
    if (smswiserDraftsOnly) {
      list = list.filter((d) => d.draftDropUniqueId != null);
    }
    return list;
  }, [drops, showCountryColumn, countryKeysFilter, smswiserDraftsOnly]);
  const sentDrops = useMemo(() => {
    let list = drops.filter((d) => d.status === 'SENT' || d.status === 'KILLED');
    if (showCountryColumn && countryKeysFilter.length > 0) {
      const set = new Set(countryKeysFilter);
      list = list.filter((d) => set.has(d.countryKey));
    }
    return list;
  }, [drops, showCountryColumn, countryKeysFilter]);

  const prevSentIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sentIds = new Set(sentDrops.map((d) => d.id));
    const newlySentIds = [...sentIds].filter((id) => !prevSentIdsRef.current.has(id));
    prevSentIdsRef.current = sentIds;
    if (newlySentIds.length === 0) return;
    setSelectedIds((prev) => {
      const remove = new Set(newlySentIds);
      const filtered = prev.filter((id) => !remove.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [sentDrops]);

  const sentCountBySection = useMemo((): Record<SentSection, number> => {
    const now = Date.now();
    const counts: Record<SentSection, number> = { sent: 0, scheduled: 0, killed: 0 };
    for (const d of sentDrops) {
      const cat = classifyPreparedDrop(d, now);
      if (cat !== 'draft') counts[cat]++;
    }
    return counts;
  }, [sentDrops]);

  const sentBySection = useMemo(() => {
    const now = Date.now();
    const sent: typeof sentDrops = [];
    const scheduled: typeof sentDrops = [];
    const killed: typeof sentDrops = [];
    for (const d of sentDrops) {
      const cat = classifyPreparedDrop(d, now);
      if (cat === 'scheduled') scheduled.push(d);
      else if (cat === 'killed') killed.push(d);
      else sent.push(d);
    }
    return { sent, scheduled, killed };
  }, [sentDrops]);

  const tagColors = dropListTags?.tagColors ?? perfTagsQuery?.data?.tagColors;
  const { data: staticTags } = useStaticTags();
  const searchLower = searchQuery.trim().toLowerCase();

  const groupSortByCountry = showCountryColumn && scheduledGroupByCountry;

  const filteredSentBySection = useMemo(() => {
    const byStatic = {
      sent: filterDropsByStaticTag(sentBySection.sent, staticTagFilters),
      scheduled: filterDropsByStaticTag(sentBySection.scheduled, staticTagFilters),
      killed: filterDropsByStaticTag(sentBySection.killed, staticTagFilters),
    };
    const bySearch = {
      sent: filterDropsBySearch(byStatic.sent, searchLower, catalog.gatewayName, catalog.leadsListName),
      scheduled: filterDropsBySearch(byStatic.scheduled, searchLower, catalog.gatewayName, catalog.leadsListName),
      killed: filterDropsBySearch(byStatic.killed, searchLower, catalog.gatewayName, catalog.leadsListName),
    };
    const sortKey = sortBy ?? 'time';
    const dir = sortDir ?? 'desc';
    const sortSection = (list: typeof bySearch.sent, section: SentSection) =>
      groupSortByCountry
        ? sortDropsGroupedByCountry(
            list,
            sortKey,
            dir,
            catalog.gatewayName,
            catalog.leadsListName,
            catalog.gatewayPricePerSms,
            section,
          )
        : sortDrops(
            list,
            sortKey,
            dir,
            catalog.gatewayName,
            catalog.leadsListName,
            catalog.gatewayPricePerSms,
            section,
          );
    return {
      sent: sortSection(bySearch.sent, 'sent'),
      scheduled: sortSection(bySearch.scheduled, 'scheduled'),
      killed: sortSection(bySearch.killed, 'killed'),
    };
  }, [sentBySection, staticTagFilters, searchLower, sortBy, sortDir, catalog, groupSortByCountry]);

  usePreparedDropInvestigationHighlight({
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
    sentBySection,
    filteredSentBySection,
    activeTab,
    sentSection,
    setActiveTab,
    setSentSection,
  });

  const displayedDropsForSelection = useMemo(() => {
    if (activeTab === 'drafts') {
      const byTag = filterDropsByTag(draftDrops, tagFilter, tagsForDrop);
      return filterDropsByStaticTag(byTag, staticTagFilters);
    }
    return filteredSentBySection[sentSection];
  }, [
    activeTab,
    draftDrops,
    tagFilter,
    tagsForDrop,
    staticTagFilters,
    sentSection,
    filteredSentBySection,
  ]);

  const tagFilterOptions = useMemo(() => {
    if (activeTab !== 'drafts') return [];
    const seen = new Set<string>(['New', 'Unused']);
    for (const d of draftDrops) {
      const tags = tagsForDrop(d) ?? [];
      for (const t of tags) {
        const s = String(t).trim();
        if (s) seen.add(s);
      }
    }
    return [...seen].sort();
  }, [activeTab, draftDrops, tagsForDrop]);

  const staticTagFilterOptions = useMemo(() => {
    if (staticTags?.length) {
      return staticTags.map((t) => t.name).sort();
    }
    const drops = activeTab === 'drafts' ? draftDrops : sentDrops;
    const seen = new Set<string>();
    for (const d of drops) {
      if (d.staticTag?.name) seen.add(d.staticTag.name);
    }
    return [...seen].sort();
  }, [staticTags, activeTab, draftDrops, sentDrops]);

  const staticTagColors = useMemo(() => {
    if (staticTags?.length) {
      return Object.fromEntries(staticTags.map((t) => [t.name, t.color]));
    }
    const drops = activeTab === 'drafts' ? draftDrops : sentDrops;
    const map: Record<string, string> = {};
    for (const d of drops) {
      if (d.staticTag?.name && d.staticTag?.color) {
        map[d.staticTag.name] = d.staticTag.color;
      }
    }
    return map;
  }, [staticTags, activeTab, draftDrops, sentDrops]);

  const filteredDrops = useMemo(
    () =>
      filterDropsBySearch(
        displayedDropsForSelection,
        searchLower,
        catalog.gatewayName,
        catalog.leadsListName,
      ),
    [displayedDropsForSelection, searchLower, catalog],
  );

  const handleSort = useCallback((key: SortKey) => {
    setSortBy((prev) => {
      if (prev === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      else setSortDir('asc');
      return key;
    });
  }, []);

  /** Scheduled section: order by executeAfter — default action is soonest first (asc). */
  const handleToggleScheduledSendTimeSort = useCallback(() => {
    if (sortBy === 'time' || sortBy === 'scheduled') {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy('time');
      setSortDir('asc');
    }
  }, [sortBy]);

  const displayedDrops = useMemo(() => {
    if (!sortBy) return filteredDrops;
    const section = activeTab === 'sent' ? sentSection : undefined;
    if (groupSortByCountry) {
      return sortDropsGroupedByCountry(
        filteredDrops,
        sortBy,
        sortDir,
        catalog.gatewayName,
        catalog.leadsListName,
        catalog.gatewayPricePerSms,
        section,
      );
    }
    return sortDrops(
      filteredDrops,
      sortBy,
      sortDir,
      catalog.gatewayName,
      catalog.leadsListName,
      catalog.gatewayPricePerSms,
      section,
    );
  }, [filteredDrops, sortBy, sortDir, catalog, activeTab, sentSection, groupSortByCountry]);

  const displayedIds = useMemo(() => displayedDrops.map((d) => d.id), [displayedDrops]);
  const dragRowSelect = useDraftRowDragSelection({
    activeTab,
    displayedDropIds: displayedIds,
    selectedIds,
    setSelectedIds,
    dragHandleSelector: '[aria-label="Drag to reorder"]',
  });
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hasSelection = selectedIds.length > 0;
  /** True when every currently visible row is selected (ignores selections hidden by filter/search). */
  const allSelected =
    displayedIds.length > 0 && displayedIds.every((id) => selectedSet.has(id));

  const selectedSentDrops = useMemo(
    () => sentDrops.filter((d) => selectedIds.includes(d.id)),
    [sentDrops, selectedIds],
  );
  const canKillOrEdit = activeTab === 'sent' && selectedSentDrops.length > 0;
  const selectedWithSentId = selectedSentDrops.filter(
    (d) => (d.sentDropId != null || d.sentDropUniqueId) && d.status === 'SENT',
  );

  const editingSentDrops = useMemo(
    () =>
      editingSentDropIds.length > 0
        ? sentDrops.filter((d) => editingSentDropIds.includes(d.id))
        : [],
    [sentDrops, editingSentDropIds],
  );

  const toggleSelected = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id),
    );
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);
  const selectAllDisplayed = useCallback(() => setSelectedIds(displayedIds), [displayedIds]);
  const handleToggleAll = useCallback(() => {
    if (allSelected) clearSelection();
    else selectAllDisplayed();
  }, [allSelected, clearSelection, selectAllDisplayed]);

  const handleToggleSectionAll = useCallback(
    (section: SentSection) => {
      const ids = filteredSentBySection[section].map((d) => d.id);
      const allSelectedInSection = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
      if (allSelectedInSection) {
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
      } else {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          for (const id of ids) next.add(id);
          return [...next];
        });
      }
    },
    [filteredSentBySection, selectedIds],
  );

  const switchToDrafts = useCallback(() => {
    setActiveTab('drafts');
    setSelectedIds([]);
    setSearchQuery('');
    setSortBy('scheduled');
    setSortDir('asc');
  }, []);
  const switchToSent = useCallback(() => {
    setActiveTab('sent');
    setSentSection('sent');
    setSelectedIds([]);
    setSearchQuery('');
    setTagFilter('');
    setSortBy('time');
    setSortDir('desc');
  }, []);

  const handleSentSectionChange = useCallback((section: SentSection) => {
    setSentSection(section);
    setSelectedIds([]);
    setSortBy('time');
    setSortDir('desc');
  }, []);

  const handleKillSent = useCallback(async () => {
    if (!onKillSent || selectedWithSentId.length === 0) return;
    await onKillSent(selectedWithSentId);
    setSelectedIds([]);
  }, [onKillSent, selectedWithSentId]);

  const handleEditSent = useCallback(async () => {
    if (selectedWithSentId.length === 0) return;
    const loadingId = toastLoading('Loading please', 'Please wait');
    try {
      const editable: string[] = [];
      for (const drop of selectedWithSentId) {
        if (onBeforeEditSent) {
          const ok = await onBeforeEditSent(drop);
          if (!ok) continue;
        }
        editable.push(drop.id);
      }
      if (editable.length === 0) return;
      const byId = new Map(drops.map((d) => [d.id, d]));
      const editableDrops = editable
        .map((id) => byId.get(id))
        .filter((d): d is (typeof drops)[number] => Boolean(d));
      if (
        showCountryColumn &&
        new Set(editableDrops.map((d) => d.countryKey)).size > 1
      ) {
        setMultiCountrySentEditIds(editable);
        return;
      }
      setEditingSentDropIds(editable);
    } finally {
      toastDismiss(loadingId);
    }
  }, [selectedWithSentId, onBeforeEditSent, toastLoading, toastDismiss, drops, showCountryColumn]);

  const handleCopyAsDraftClick = useCallback(() => {
    if (selectedSentDrops.length > 0) setShowCopyAsDraft(true);
  }, [selectedSentDrops.length]);

  const handleDuplicateDraftClick = useCallback(() => {
    if (activeTab === 'drafts' && selectedIds.length > 0) setShowDuplicateDraft(true);
  }, [activeTab, selectedIds.length]);

  const handleDuplicateDraftConfirm = useCallback(
    async (copiesPerDraft: number, targetWorkDate: string) => {
      if (!onDuplicateDraft || selectedIds.length === 0) return;
      const selectedDrafts = draftDrops.filter((d) => selectedIds.includes(d.id));
      await onDuplicateDraft(selectedDrafts, copiesPerDraft, targetWorkDate);
      setSelectedIds([]);
      setShowDuplicateDraft(false);
    },
    [onDuplicateDraft, selectedIds, draftDrops],
  );

  const handleCopyAsDraftConfirm = useCallback(
    async (targetWorkDate: string) => {
      if (!onCopyAsDraft || selectedSentDrops.length === 0) return;
      await onCopyAsDraft(selectedSentDrops, targetWorkDate);
      setSelectedIds([]);
      setShowCopyAsDraft(false);
    },
    [onCopyAsDraft, selectedSentDrops],
  );

  const bulkSendCallbacks = useMemo(
    () => ({
      toastLoading,
      toastUpdate,
      toastDismiss,
      toastSuccess,
      toastError,
      setBulkSendStatus,
      setSelectedIds,
      onBulkSendComplete,
    }),
    [
      toastLoading,
      toastUpdate,
      toastDismiss,
      toastSuccess,
      toastError,
      onBulkSendComplete,
    ],
  );

  const handleBulkSend = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (onBulkSend) {
      await runBulkSendViaCallback(selectedIds, onBulkSend, bulkSendCallbacks);
      return;
    }
    if (wiserId == null) return;
    await runBulkSendViaStream(countryKey, wiserId, selectedIds, bulkSendCallbacks);
  }, [countryKey, selectedIds, wiserId, onBulkSend, bulkSendCallbacks]);

  const handleDelete = useCallback(async () => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length > PREPARED_DRAFTS_BULK_DELETE_CONFIRM_ABOVE_COUNT) {
      if (!window.confirm(bulkDeleteConfirmMessage(selectedIds.length))) return;
    }
    const count = selectedIds.length;
    await onDelete(selectedIds);
    setSelectedIds([]);
    // The rows just vanish otherwise — say what happened and where the way back
    // is, since a small delete never asked for confirmation in the first place.
    toastSuccess(
      `Deleted ${count} draft${count === 1 ? '' : 's'}`,
      count > PREPARED_DRAFTS_UNDOABLE_MAX_COUNT
        ? 'Too many to undo.'
        : 'Use "Undo last change" in the toolbar to put them back.',
    );
  }, [onDelete, selectedIds, toastSuccess]);

  const requestOpenBulkEdit = useCallback(() => {
    if (showCountryColumn && selectedIds.length > 0) {
      const sel = draftDrops.filter((d) => selectedIds.includes(d.id));
      const keys = new Set(sel.map((d) => d.countryKey));
      if (keys.size > 1) {
        setShowMultiCountryEdit(true);
        return;
      }
    }
    setShowBulkEdit(true);
  }, [showCountryColumn, selectedIds, draftDrops]);

  return {
    ...props,
    activeTab,
    sentSection,
    draftDrops,
    sentDrops,
    sentCountBySection,
    sentBySection: filteredSentBySection,
    displayedDrops,
    displayedDropsForSelection,
    displayedIds,
    selectedIds,
    selectedSet,
    hasSelection,
    allSelected,
    selectedSentDrops,
    canKillOrEdit,
    selectedWithSentId,
    editingSentDrops,
    showCopyAsDraft,
    showDuplicateDraft,
    showBulkEdit,
    showMultiCountryEdit,
    multiCountrySentEditIds,
    setMultiCountrySentEditIds,
    searchQuery,
    tagFilter,
    tagFilterOptions,
    tagColors,
    tagsLoading: dropListTags?.isLoading ?? perfTagsQuery?.isLoading ?? false,
    staticTagFilters,
    staticTagFilterOptions,
    staticTagColors,
    bulkSendStatus,
    sortBy,
    sortDir,
    catalog,
    tagsForDrop,
    showCountryColumn,
    countryFilterOptions,
    countryKeysFilter,
    setCountryKeysFilter,
    scheduledGroupByCountry,
    setScheduledGroupByCountry,
    smswiserDraftsOnly,
    setSmswiserDraftsOnly,
    setSearchQuery,
    setTagFilter,
    setStaticTagFilters,
    handleSentSectionChange,
    setEditingSentDropIds,
    setShowCopyAsDraft,
    setShowDuplicateDraft,
    setShowBulkEdit,
    setShowMultiCountryEdit,
    requestOpenBulkEdit,
    handleSort,
    toggleSelected,
    clearSelection,
    handleToggleAll,
    handleToggleSectionAll,
    switchToDrafts,
    switchToSent,
    handleKillSent,
    handleEditSent,
    handleCopyAsDraftClick,
    handleDuplicateDraftClick,
    handleDuplicateDraftConfirm,
    handleCopyAsDraftConfirm,
    handleBulkSend,
    handleDelete,
    handleToggleScheduledSendTimeSort,
    onDraftRowPointerDown: dragRowSelect.onRowPointerDown,
    onDraftRowPointerEnter: dragRowSelect.onRowPointerEnter,
    consumeDraftRowClickSuppress: dragRowSelect.consumeSuppressClick,
  };
}
